import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Menu, Moon, PenLine, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AppShell, BrandMark, Button } from '@argink/ui';
import { useAuth } from '../../auth/AuthContext';
import { useThemePreference } from '../../lib/theme';
import { LinkButton } from './LinkButton';
import { GoToDialog, GoToTrigger, useGoToPalette } from './GoToPalette';
import { NEW_ENVELOPE_LABEL, OPEN_TEST_INBOX_LABEL, PRODUCT_NAV } from '../../lib/copy';

const MAILPIT_URL = 'http://127.0.0.1:8026';

/** Resolve a CHAVE de ícone de `PRODUCT_NAV` (dado puro, sem React) para o
 * componente Lucide real — é este arquivo, não `lib/copy.ts`, que sabe que o
 * app usa `lucide-react` (R-UX02-P01/D-101: o DS não sabe disso, e `copy.ts`
 * continua sem import de React). */
const NAV_ICON: Record<'dashboard' | 'envelopes', LucideIcon> = {
  dashboard: LayoutDashboard,
  envelopes: PenLine,
};

/** A única rota onde o CTA "Novo envio" NÃO aparece no shell: criar um novo
 * envio já É a tela; repetir o CTA ali seria o botão levar para onde já se
 * está (§"NAVEGAÇÃO" do contrato). */
const NEW_ENVELOPE_ROUTE = '/envelopes/new';

/**
 * A casca do ambiente autenticado.
 *
 * **Não é um shell novo.** É a composição do `AppShell` de `@argink/ui` (que é quem
 * sabe ser gaveta no mobile, coluna no desktop, e gerenciar o foco disso) com as
 * únicas duas coisas que o design system não pode saber: as ROTAS deste app e a
 * SESSÃO deste usuário. O DS não depende de react-router de propósito — ver o
 * comentário de `AppShell.tsx` no package.
 *
 * O header horizontal que existia aqui antes foi removido, não somado: dois
 * shells concorrentes é o que o §9/§33 do mandato proíbem, e era ele que dava ao
 * ambiente autenticado cara de página solta em vez de produto.
 */
export interface TenantShellProps {
  /** Título da página. Vive no header do shell, não dentro do conteúdo — é ele
   * que responde "onde estou" quando a sidebar responde "o que existe". */
  title: string;
  /** Linha de contexto sob o título. Opcional: nem toda tela tem o que dizer. */
  subtitle?: ReactNode;
  /** Ações da página, à direita do header (tipicamente o CTA primário). */
  actions?: ReactNode;
  children: ReactNode;
}

export function TenantShell({ title, subtitle, actions, children }: TenantShellProps) {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useThemePreference();
  const goTo = useGoToPalette();

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  /* O CTA "Novo envio" fica disponível no shell inteiro, EXCETO dentro da
   * própria tela de criação (§"NAVEGAÇÃO" do contrato R-UX02-P01/D-101) — repetir
   * o botão ali levaria para onde a pessoa já está. Antes desta lane, o CTA era
   * responsabilidade de CADA página (`actions={<LinkButton to="/envelopes/new">…}`
   * em `DashboardPage`/`EnvelopesPage`, ausente nas demais); agora é do shell, e
   * as duas páginas que o duplicavam pararam de passar `actions` para isto. */
  const showNewEnvelopeCta = location.pathname !== NEW_ENVELOPE_ROUTE;

  return (
    /* `.argink-theme-scope` (R-UX02-P01/L3A-FIX, `@argink/ui`/tokens.css): envolve o
     * `AppShell` E o `GoToDialog` — o `<dialog>` da paleta é irmão do shell de propósito
     * (ver comentário em `GoToPalette.tsx`), então o tema escuro só chega até ele através
     * deste ancestral comum. `display: contents` (tokens.css) garante que o wrapper não
     * introduz nenhuma caixa de layout — a única caixa real continua sendo a do
     * `AppShell`. Nunca `data-theme` em `html`/`body`/`:root`: este `<div>` só existe no
     * ambiente autenticado, do mesmo jeito que `.argink-app-shell` já era. */
    <div className="argink-theme-scope" data-theme={theme}>
      <AppShell
        theme={theme}
        sidebarTriggerIcon={<Menu size={20} />}
        /* A gaveta fica **não controlada**: cada rota do tenant monta o seu próprio
         * shell, então o estado já nasce fechado a cada navegação. Um `useEffect`
         * fechando-a em `location.pathname` foi escrito, sabotado e removido — a
         * suíte continuava verde com ele quebrado, porque quem fecha a gaveta é a
         * remontagem. Trava que não muda nada quando some é enfeite, não guarda.
         *
         * O teste "trocar de rota fecha a gaveta" fica: ele mede o COMPORTAMENTO
         * visível, e é ele que vai acusar se um dia o shell subir para layout de
         * rota (com `<Outlet/>`) e passar a sobreviver à navegação. */
        sidebarHeader={
          <NavLink
            to="/dashboard"
            className="tenant-nav__brand"
            aria-label="ARGINK Sign — Painel"
          >
            {/* `wordmark`, e não o lockup: a sidebar tem 232px e o lockup narrativo
             * exige ≥320px (brandbook §8). O próprio PROVENANCE.md dos assets
             * prescreve o wordmark tipográfico para espaços abaixo desse mínimo —
             * não existe SVG master nem símbolo isolado no repo, e desenhar um
             * seria redesenhar a marca. "Sign" é texto composto ao lado, não um
             * novo asset (§"MARCA" do contrato) — `aria-hidden`, porque o nome
             * acessível inteiro já está no `aria-label` do link. */}
            <BrandMark variant="wordmark" />
            <span className="tenant-nav__brand-suffix" aria-hidden="true">
              Sign
            </span>
          </NavLink>
        }
        sidebarContent={
          <>
            <GoToTrigger palette={goTo} />
            {PRODUCT_NAV.map((item) => {
              const Icon = NAV_ICON[item.icon];
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `tenant-nav__link argink-focusable${isActive ? ' tenant-nav__link--active' : ''}`
                  }
                >
                  <Icon aria-hidden="true" size={18} />
                  <span className="tenant-nav__label">{item.label}</span>
                </NavLink>
              );
            })}
          </>
        }
        sidebarFooter={
          <div className="tenant-account">
            {session ? (
              <div className="tenant-account__identity">
                <span className="tenant-account__name">{session.user.name}</span>
                <span className="tenant-account__email mono">{session.user.email}</span>
              </div>
            ) : null}
            {session?.environment === 'development' ? (
              <a
                className="tenant-account__dev-link argink-focusable"
                href={MAILPIT_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                {OPEN_TEST_INBOX_LABEL}
              </a>
            ) : null}
            {/* Logout continua SÓ aqui, no rodapé (R-UX02-P01/L3A-FIX §"TEMA" do
              * contrato) — o toggle de tema saiu daqui e foi para `headerActions`
              * abaixo, único lugar onde ele existe agora (nunca duplicado). */}
            <div className="tenant-account__actions">
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Sair
              </Button>
            </div>
          </div>
        }
        headerContent={
          <div className="tenant-header">
            <h1 className="tenant-header__title">{title}</h1>
            {subtitle ? <p className="tenant-header__subtitle">{subtitle}</p> : null}
          </div>
        }
        headerActions={
          <>
            {/* Toggle de tema na TOPBAR, antes do CTA (R-UX02-P01/L3A-FIX, achado
              * D-101 residual: antes vivia só no rodapé da sidebar/gaveta, inacessível
              * com a gaveta fechada — `headerActions` nunca fica atrás da gaveta em
              * nenhum viewport). Único lugar onde existe: não duplicar. */}
            <button
              type="button"
              className="theme-toggle argink-focusable"
              onClick={toggleTheme}
              aria-pressed={theme === 'dark'}
              aria-label={
                theme === 'dark' ? 'Usar tema claro nesta sessão' : 'Usar tema escuro nesta sessão'
              }
            >
              {theme === 'dark' ? (
                <Moon aria-hidden="true" size={16} />
              ) : (
                <Sun aria-hidden="true" size={16} />
              )}
            </button>
            {showNewEnvelopeCta ? (
              <LinkButton to={NEW_ENVELOPE_ROUTE}>{NEW_ENVELOPE_LABEL}</LinkButton>
            ) : null}
            {actions}
          </>
        }
      >
        {/* O `main` do shell sangra de borda a borda de propósito (é ele quem
          * encosta na sidebar). A medida de leitura do CONTEÚDO é decisão do app —
          * uma tabela de envios a 2560px sem teto vira uma linha de dados perdida
          * no branco. */}
        <div className="tenant-content">{children}</div>
      </AppShell>
      {/* Irmão do AppShell, nunca dentro da sidebar — ver o comentário no topo de
        * `GoToPalette.tsx` (a gaveta fechada no mobile aplica `visibility: hidden`
        * na sidebar, e um `<dialog>` ali dentro herdaria isso mesmo com
        * `showModal()`). Dentro de `.argink-theme-scope` (acima) para herdar o
        * mesmo tema do `AppShell` vizinho. */}
      <GoToDialog palette={goTo} />
    </div>
  );
}
